/**
 * The pre-release testing agreement every participant reads and signs before
 * using the app. One copy, read by the app (to show it) and the worker (to
 * fingerprint exactly what was signed).
 *
 * Changing the wording? Bump AGREEMENT_VERSION — everyone signs again on their
 * next visit, and each signature stays tied to the text it was given.
 *
 * The one bracketed item left, the privacy policy [URL], is still to be filled in.
 */
export const AGREEMENT_VERSION = '2026-10-06';
export const AGREEMENT_TITLE = 'Pre-release Testing Agreement';

export const AGREEMENT_PARTIES =
  'This agreement is between Silent Foundry Pty Ltd, trading as Silentfoundry.ai (the Company) and the individual identified in the signature block (the Participant).';
export const AGREEMENT_PREAMBLE = 'In return for access to the App, the Participant agrees as follows.';

export const AGREEMENT_SECTIONS: { title: string; paras: string[] }[] = [
  {
    title: 'Background',
    paras: [
      "The Company is developing a software application that produces a property market report to assist buyers (the App). The Participant wishes to take part in pre-release testing. This agreement sets the terms of that participation and protects the Company's confidential information and intellectual property.",
    ],
  },
  {
    title: 'Definitions',
    paras: [
      "Confidential Information means all non-public information disclosed to or observed by the Participant in connection with the App, including: the App itself and its features, screens and outputs; the existence, name and branding of the product; the report methodology, calculations, data sources and any estimated values or ranges; pricing, roadmap and commercial plans; and the fact and content of testing. It does not include information that is or becomes public other than through the Participant's breach.",
      'Research partner means a contractor engaged by the Company to run or observe the test, and identified to the Participant before any session is recorded.',
    ],
  },
  {
    title: 'Confidentiality',
    paras: [
      'The Participant must keep all Confidential Information strictly confidential and use it only to participate in testing and provide feedback.',
      'The Participant must not disclose Confidential Information to any other person, or make any public statement, post, review or social-media reference about the App or the testing.',
      'The Participant may disclose Confidential Information to a legal adviser or accountant who needs it and is bound to keep it confidential, and to the extent required by law, provided that, where legally permitted, the Participant tells the Company first and limits the disclosure to what is required.',
    ],
  },
  {
    title: 'Use',
    paras: [
      'Access is free, personal, limited and revocable. The Company may end it at any time. It is not a promise that the App will be released.',
      'The Participant must not copy, screenshot, screen-record, download or keep any part of the App or its outputs, except a screenshot or note sent only to the Company or its research partner as feedback. Those copies must be deleted when testing ends.',
      'The Participant must not share access, or give any test build to any other person.',
      'The Participant must not reverse engineer, decompile, scrape, or deliberately attempt to extract the methodology, calculations or data sources. Sending feedback about what the App did is not a breach.',
      'The Participant may run the App on a real address. The Participant must not rely on the App or its outputs to buy, sell, offer, borrow, or appoint an agent or other adviser.',
    ],
  },
  {
    title: 'Feedback',
    paras: [
      "Feedback is voluntary. The Participant grants the Company a permanent, irrevocable, worldwide, royalty-free licence to use it for any purpose connected with the Company's business. To the extent permitted by the Copyright Act 1968 (Cth), the Participant consents to the Company using feedback without naming the Participant.",
    ],
  },
  {
    title: 'Intellectual property',
    paras: [
      "All intellectual property in the App and in Confidential Information remains the Company's. Nothing in this agreement transfers any of it to the Participant.",
    ],
  },
  {
    title: 'Beta — no warranty, no reliance',
    paras: [
      'The App is provided as is, for testing. It is pre-release and may contain errors. Any estimated value or range is general information for testing only. It is not a valuation and it is not financial, legal or property advice.',
      'The Participant must not rely on the App or its outputs for any decision. The Company excludes all liability to the maximum extent permitted by law. Nothing in this agreement excludes rights that cannot be excluded under the Australian Consumer Law.',
    ],
  },
  {
    title: 'Privacy and recordings',
    paras: [
      "The Participant consents to the Company and its research partner collecting the Participant's name, contact details, addresses submitted, report outputs and feedback, and, where the Participant is told beforehand, recording a test session.",
      "That information is handled under the Company's privacy policy at [URL]. The Participant should not have another person visible or audible in a recorded session unless that person knows.",
      'The Company may use results in de-identified, aggregate form with investors and partners. A particular participant, and a particular address they tested, will not be identifiable.',
    ],
  },
  {
    title: 'Term',
    paras: [
      'The Participant must be 18 or over.',
      'Confidentiality starts on signing and continues for 3 years after testing ends. Testing ends when the Company says so in writing, or when the App is made available to the public, whichever is earlier.',
      'On request, or when testing ends, the Participant must stop using the App, delete any test build, and delete or destroy Confidential Information in their control, including feedback screenshots.',
    ],
  },
  {
    title: 'Remedies',
    paras: [
      'The Participant acknowledges that a breach may cause harm that damages alone may not fix, and that the Company may seek an injunction as well as any other remedy.',
    ],
  },
  {
    title: 'General',
    paras: [
      'This agreement is governed by the laws of Queensland. The parties submit to the non-exclusive jurisdiction of the courts of Queensland.',
      'It is the entire agreement on this subject. The Participant may not assign it. It may be signed electronically.',
    ],
  },
];

/** The agreement as one plain text — what is fingerprinted when someone signs. */
export function agreementText(): string {
  return [
    `${AGREEMENT_TITLE} (version ${AGREEMENT_VERSION})`,
    AGREEMENT_PARTIES,
    AGREEMENT_PREAMBLE,
    ...AGREEMENT_SECTIONS.map((s, i) => `${i + 1}. ${s.title}\n${s.paras.join('\n')}`),
  ].join('\n\n');
}
